import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { connectDjpal } from '../../redux/thunk/djpalThunk';
import { followArtist, unfollowArtist } from '../../redux/thunk/followedArtistsThunk';
import { setActiveTab, setActiveCategory, setOnboardingActive, setOnboardingDecided } from '../../redux/slices/appSlice';
import { refreshAndRetry } from '../../redux/utils/refreshAndRetry';
import { track } from '../../utils/analytics';
import MixGoatLogo from '../MixGoatLogo';
import { useDesignV2 } from '../../utils/designV2';
import rekordboxLogo from '../../assets/rekordbox-logo.png';
import seratoLogo from '../../assets/serato-logo-10px.png';
import traktorLogo from '../../assets/traktor-logo.png';
import virtualdjLogo from '../../assets/virtual-dj-logo.png';
import './OnboardingFlow.scss';
import CoverArt from '../CoverArt';

const isWeb = typeof window !== 'undefined' && window.electron && !window.electron.db;

// Per-(user, device) localStorage key — set once the user has been offered
// the "import your DJ library" step on this machine FOR THIS ACCOUNT. The
// scope is intentionally combined: the DJ software install is per-device
// (so we don't re-pester on every relaunch), but a different account
// signing into the same machine has its own library on the server and
// deserves its own one-time prompt. Same account on a second device also
// gets its own prompt because the key is namespaced per device.
const IMPORT_DONE_KEY_PREFIX = 'mixgoat-onboarding-import-v1:';
const importDoneKey = (userId) => `${IMPORT_DONE_KEY_PREFIX}${userId}`;

// Per-(user) localStorage flag mirroring the artists step's server-side
// `artistsOnboardedAt`. The server flag is the cross-device source of
// truth, but the write at complete() is fire-and-forget — if that PATCH
// is lost (flaky network, app backgrounded right after) or getPreferences
// errors on the next launch, the user would see onboarding AGAIN. This
// local flag is written synchronously at complete() and checked first in
// decide(), so once a user finishes onboarding on a device it never
// re-shows there regardless of server-call reliability.
const ARTISTS_DONE_KEY_PREFIX = 'mixgoat-onboarding-artists-v1:';
const artistsDoneKey = (userId) => `${ARTISTS_DONE_KEY_PREFIX}${userId}`;

// iOS WKWebView evicts localStorage under memory pressure (the same eviction
// that wiped auth tokens — see capacitor-shim.js recoverSession). When that
// flag vanishes, a returning user is re-shown onboarding. So on native we
// ALSO mirror the artists-done flag into the durable Capacitor Preferences
// store (window.electron.store, backed by native Preferences), which survives
// eviction. No-op on desktop/web — there localStorage is already durable, so
// durableStore is null and these helpers just use localStorage.
const durableStore = (typeof window !== 'undefined' && window.electron && window.electron.store) || null;
function readArtistsDone(userId) {
    if (!userId) return false;
    try { if (localStorage.getItem(artistsDoneKey(userId))) return true; } catch {}
    try { if (durableStore && durableStore.get(artistsDoneKey(userId))) return true; } catch {}
    return false;
}
function writeArtistsDone(userId) {
    if (!userId) return;
    try { localStorage.setItem(artistsDoneKey(userId), 'done'); } catch {}
    try { if (durableStore) durableStore.set(artistsDoneKey(userId), 'done'); } catch {}
}

// Legacy device-wide keys from earlier cuts of this flow. They aren't
// namespaced by user, so we can't tell WHICH account completed them.
// We just delete them on sight rather than back-fill against the current
// user (the back-fill version mis-suppressed the import prompt for any
// fresh account signing in after legacy flows had been completed on the
// machine — the symptom this comment exists to prevent regressing). The
// original legacy-completer sees the prompt one more time per device;
// every other user gets correct first-prompt behaviour.
const LEGACY_IMPORT_KEY = 'mixgoat-onboarding-import-v1';
const LEGACY_ONBOARDING_KEY = 'mixgoat-onboarding-v1';

const PLATFORMS = [
    { key: 'rekordbox', name: 'Rekordbox', logo: rekordboxLogo },
    { key: 'serato', name: 'Serato', logo: seratoLogo },
    { key: 'traktor', name: 'Traktor', logo: traktorLogo },
    { key: 'virtualdj', name: 'VirtualDJ', logo: virtualdjLogo },
];

// ─── Icons ───────────────────────────────────────────────────────────────────

const StarIcon = ({ filled }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
);

const CheckIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const XIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);

const MusicIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
    </svg>
);

const LibraryIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>
);

const MigrateIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
    </svg>
);

// ─── Step: Welcome ────────────────────────────────────────────────────────────

const WelcomeStep = ({ onNext }) => {
    const v2 = useDesignV2();
    return (
    <div className="ob-step welcome-step">
        <div className="ob-logo-wrap">
            <MixGoatLogo width={100} height={100} />
            <div className="ob-logo-glow" />
        </div>
        <h1 className="ob-title">
            Welcome to <span className="ob-accent">MixGoat</span>
        </h1>
        {v2 ? (
            <>
                <p className="ob-tagline">Steal any DJ's secret weapons. See who plays yours.</p>
                <p className="ob-desc">
                    Connect your Rekordbox, Serato or Traktor library and MixGoat shows every track your favourite DJs play, who's playing yours, and a feed built from your own crates.
                </p>
            </>
        ) : (
            <>
                <p className="ob-tagline">Your DJ toolkit, reimagined.</p>
                <p className="ob-desc">
                    Discover music, manage your library, and stay in sync with the scene — all in one place.
                </p>
            </>
        )}
        <button className="ob-btn ob-btn-primary" onClick={onNext}>
            Let's go
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
            </svg>
        </button>
    </div>
);
};

// ─── Step: Import Library (desktop only) ─────────────────────────────────────

const ImportStep = ({ detectedPlatforms, onNext }) => {
    const v2 = useDesignV2();
    const anyDetected = Object.values(detectedPlatforms).some(p => p?.exists);
    const detectedPlatformsList = PLATFORMS.filter(p => detectedPlatforms[p.key]?.exists);

    const [importState, setImportState] = useState('idle'); // idle | importing | done | error
    const [currentPlatform, setCurrentPlatform] = useState(null);
    const [results, setResults] = useState([]);

    const handleImport = async () => {
        setImportState('importing');
        setResults([]);
        const importResults = [];

        for (const platform of detectedPlatformsList) {
            setCurrentPlatform(platform.key);
            try {
                let result;
                if (platform.key === 'rekordbox') {
                    const path = detectedPlatforms.rekordbox?.path;
                    result = await window.electron.rekordbox.importPlaylists(path);
                } else if (platform.key === 'serato') {
                    result = await window.electron.serato.importPlaylists();
                } else if (platform.key === 'traktor') {
                    result = await window.electron.traktor.importPlaylists();
                } else if (platform.key === 'virtualdj') {
                    result = await window.electron.virtualdj.importPlaylists();
                }
                importResults.push({ platform, success: true, result });
            } catch (err) {
                importResults.push({ platform, success: false, error: err.message });
            }
        }

        setCurrentPlatform(null);
        setResults(importResults);
        setImportState('done');
    };

    return (
        <div className="ob-step import-step">
            <div className="ob-step-icon">
                <MigrateIcon />
            </div>
            <h2 className="ob-step-title">Import your library</h2>

            {anyDetected ? (
                <>
                    {importState === 'idle' && (
                        <p className="ob-step-subtitle">
                            {v2
                                ? "MixGoat lines your crates up against real DJ mixes. That's what builds your feed and shows you who plays your tracks."
                                : <>Connect your DJ software to unlock <strong>instant track recommendations</strong> and seamless library management across all your apps.</>}
                        </p>
                    )}

                    <div className="platform-grid-centered">
                        {detectedPlatformsList.map(platform => {
                            const r = results.find(x => x.platform.key === platform.key);
                            const isActive = currentPlatform === platform.key;
                            return (
                                <div key={platform.key} className={`platform-card detected ${isActive ? 'importing' : ''} ${r ? (r.success ? 'import-success' : 'import-error') : ''}`}>
                                    <div className="platform-logo-wrap">
                                        <img src={platform.logo} alt={platform.name} />
                                    </div>
                                    <span className="platform-name">{platform.name}</span>
                                    {isActive ? (
                                        <span className="platform-badge importing-badge">
                                            <span className="mini-spinner" />
                                            Importing...
                                        </span>
                                    ) : r ? (
                                        <span className={`platform-badge ${r.success ? 'detected-badge' : 'error-badge'}`}>
                                            {r.success ? <CheckIcon /> : <XIcon />}
                                            {r.success ? 'Imported' : 'Failed'}
                                        </span>
                                    ) : (
                                        <span className="platform-badge detected-badge">
                                            <CheckIcon />
                                            Found
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {importState === 'importing' && (
                        <div className="import-progress-bar">
                            <div
                                className="import-progress-fill"
                                style={{ width: `${Math.round((results.length / detectedPlatformsList.length) * 100)}%` }}
                            />
                        </div>
                    )}

                    {importState === 'idle' && (
                        <>
                            <button className="ob-btn ob-btn-primary" onClick={handleImport}>
                                Import now
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                                </svg>
                            </button>
                            <button className="ob-btn ob-btn-ghost" onClick={onNext}>
                                {v2 ? 'Later' : 'Later from sidebar'}
                            </button>
                        </>
                    )}

                    {importState === 'done' && (
                        <button className="ob-btn ob-btn-primary" onClick={onNext}>
                            Continue
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                            </svg>
                        </button>
                    )}
                </>
            ) : (
                <>
                    <p className="ob-step-subtitle">
                        {v2
                            ? "MixGoat lines your crates up against real DJ mixes. That's what builds your feed and shows you who plays your tracks."
                            : <>Link your DJ library for <strong>instant recommendations</strong>, playlist migration, and cue point sync — all in one place.</>}
                    </p>
                    <p className="ob-hint">{v2
                        ? 'No DJ software found on this computer. You can connect Rekordbox, Serato, Traktor or VirtualDJ any time from the sidebar.'
                        : <>No DJ software found on this computer — you can connect your library anytime from the <strong>sidebar</strong>.</>}</p>
                    <button className="ob-btn ob-btn-primary" onClick={onNext}>
                        Continue
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                        </svg>
                    </button>
                </>
            )}
        </div>
    );
};

// ─── Step: Follow Artists ─────────────────────────────────────────────────────

const ArtistCard = ({ artist, followed, onToggle }) => {
    const initials = (artist.name || '?').charAt(0).toUpperCase();
    return (
        <button
            className={`artist-card ${followed ? 'followed' : ''}`}
            onClick={() => onToggle(artist)}
            title={artist.name}
        >
            <div className="artist-avatar">
                {artist.artworkUrl ? (
                    <CoverArt src={artist.artworkUrl} size={80} alt={artist.name} />
                ) : (
                    <div className="avatar-initial">{initials}</div>
                )}
                <div className={`follow-overlay ${followed ? 'followed' : ''}`}>
                    <StarIcon filled={followed} />
                </div>
            </div>
            <span className="artist-name">{artist.name}</span>
        </button>
    );
};

// Search result row (list view)
const ArtistRow = ({ artist, followed, onToggle }) => {
    const initials = (artist.name || '?').charAt(0).toUpperCase();
    return (
        <button
            className={`artist-row ${followed ? 'followed' : ''}`}
            onClick={() => onToggle(artist)}
        >
            <div className="artist-row-avatar">
                {artist.artworkUrl
                    ? <CoverArt src={artist.artworkUrl} size={80} alt={artist.name} />
                    : <div className="avatar-initial">{initials}</div>
                }
            </div>
            <span className="artist-row-name">{artist.name}</span>
            <span className={`artist-row-star ${followed ? 'filled' : ''}`}>
                <StarIcon filled={followed} />
            </span>
        </button>
    );
};

const ArtistsStep = ({
    popularArtists, searchResults, searchQuery, onSearchChange, localFollowed,
    onFollowToggle, isLoading, onNext,
}) => {
    const v2 = useDesignV2();
    const followCount = localFollowed.size;
    const isSearching = searchQuery.trim().length > 0;
    const listToShow = isSearching ? searchResults : popularArtists;

    return (
        <div className="ob-step artists-step">
            <h2 className="ob-step-title" style={{ marginTop: 28 }}>{v2 ? 'Follow a few DJs' : "Who's your vibe?"}</h2>
            <p className="ob-step-subtitle">
                {v2
                    ? 'Your feed starts with what they play. Turn on the bell on any artist to hear about new mixes.'
                    : 'Follow DJs to build your personalised feed. Turn on the bell on any artist for mix alerts.'}
            </p>

            <div className="artists-search-wrap">
                <input
                    type="text"
                    className="artists-search-input"
                    placeholder={v2 ? 'Search DJs' : 'Search DJs...'}
                    value={searchQuery}
                    onChange={e => onSearchChange(e.target.value)}
                />
                {searchQuery
                    ? <button className="search-clear-btn" onClick={() => onSearchChange('')} aria-label="Clear">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    : <span className="search-icon-wrap"><SearchIcon /></span>
                }
            </div>

            {!isSearching && (
                <p className="artists-section-label">Trending DJs</p>
            )}

            <div className="artists-list-scroll">
                {isLoading ? (
                    <div className="artists-loading">
                        <div className="loading-spinner" />
                    </div>
                ) : listToShow.length === 0 && isSearching ? (
                    <div className="artists-empty"><p>No results for "{searchQuery}"</p></div>
                ) : (
                    <div className="artists-list">
                        {listToShow.map(artist => (
                            <ArtistRow
                                key={artist.name}
                                artist={artist}
                                followed={localFollowed.has(artist.name)}
                                onToggle={onFollowToggle}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="artists-footer">
                {followCount > 0 ? (
                    <span className="follow-count-badge">
                        <StarIcon filled />
                        {followCount} followed
                    </span>
                ) : (
                    <span className="follow-hint">{v2 ? 'Click a DJ to follow' : 'Tap to follow'}</span>
                )}
                {/* v2: amber only once there's a next step worth taking; skipping is the quiet option. */}
                <button className={`ob-btn ${v2 && followCount === 0 ? 'ob-btn-ghost' : 'ob-btn-primary'}`} onClick={onNext}>
                    {followCount > 0 ? 'Continue' : 'Skip'}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                    </svg>
                </button>
            </div>
        </div>
    );
};

// ─── Step: Done ───────────────────────────────────────────────────────────────

const DoneStep = ({ onComplete, followCount }) => {
    const v2 = useDesignV2();
    return (
    <div className="ob-step done-step">
        <div className="done-checkmark">
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="26" stroke="var(--color-accent)" strokeWidth="3" className="check-circle" />
                <path d="M16 28 L24 36 L40 20" stroke="var(--color-success)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="check-path" />
            </svg>
        </div>
        <h2 className="ob-step-title">{v2 ? "You're all set" : "You're all set!"}</h2>
        {followCount > 0 && (
            <p className="ob-step-subtitle">
                Following {followCount} DJ{followCount !== 1 ? 's' : ''} — your feed is ready.
            </p>
        )}

        <div className="feature-cards-row">
            <div className="feature-mini-card">
                <div className="fmc-icon"><MusicIcon /></div>
                <div className="fmc-label">For You</div>
                <div className="fmc-desc">{v2 ? 'Your feed' : 'Personalised feed'}</div>
            </div>
            <div className="feature-mini-card">
                <div className="fmc-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                </div>
                <div className="fmc-label">Mixpedia</div>
                <div className="fmc-desc">{v2 ? 'Every DJ, mix and track' : 'Artist discovery'}</div>
            </div>
            {!isWeb && (
                <div className="feature-mini-card">
                    <div className="fmc-icon"><LibraryIcon /></div>
                    <div className="fmc-label">Library</div>
                    <div className="fmc-desc">{v2 ? 'Your crates, synced' : <>Manage &amp; migrate</>}</div>
                </div>
            )}
        </div>

        <button className="ob-btn ob-btn-primary ob-btn-large" onClick={onComplete}>
            Start exploring
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
            </svg>
        </button>
    </div>
);
};

// ─── Main: OnboardingFlow ─────────────────────────────────────────────────────

const OnboardingFlow = ({ appReady }) => {
    const dispatch    = useDispatch();
    const djpalTokenRedux = useSelector(state => state.djpal.token);
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const accessToken = useSelector(state => state.auth.accessToken); // null on web — shim uses cookies
    const currentUserId = useSelector(state => state.auth.user?.id || null);

    // Read token from Redux OR localStorage (like ArtistSearch.js does)
    const djpalToken = djpalTokenRedux || localStorage.getItem('djpal:token');

    const [visible, setVisible]   = useState(false);
    const [step, setStep]         = useState(0);
    const [exiting, setExiting]   = useState(false);

    // Step list is computed once we know which sub-flows the user still
    // needs (import per-device, artists per-account). Empty until decided.
    const [steps, setSteps] = useState([]);

    // Whether the artists step has already been completed on the account
    // (server-side flag). Used to skip the server-write on `complete()` if
    // it's already done — avoids a noisy no-op PATCH.
    const artistsAlreadyDoneRef = useRef(false);

    // The user id we last ran the decision effect for. The component stays
    // mounted across sign-out / sign-in, so guarding with a plain boolean
    // would short-circuit the second user's flow. Tracking the id lets a
    // user change re-trigger the decision (and clear residual UI state).
    const decidedForUserIdRef = useRef(null);

    // Import step
    const [detectedPlatforms, setDetectedPlatforms] = useState({});

    // Artists step
    const [popularArtists, setPopularArtists]   = useState([]);
    const [searchQuery, setSearchQuery]         = useState('');
    const [searchResults, setSearchResults]     = useState([]);
    const [localFollowed, setLocalFollowed]     = useState(new Set());
    const [isLoadingArtists, setIsLoadingArtists] = useState(false);

    const searchTimeout = useRef(null);
    const currentStep   = steps[step];

    // ── Decide whether (and which steps) to show ────────────────────────────
    // Two independent signals:
    //   1. import:  per-(user, device) localStorage at importDoneKey(userId).
    //               One shot per account on each desktop they sign into.
    //   2. artists: per-account server flag `artistsOnboardedAt`. One shot
    //               per account across all devices.
    // If both say "done", we show nothing. Welcome/Done are scaffolding —
    // only included when there's at least one real step to wrap.
    useEffect(() => {
        if (!isAuthenticated || !appReady) return;
        if (!currentUserId) return; // Wait until the user object hydrates
        if (decidedForUserIdRef.current === currentUserId) return;
        decidedForUserIdRef.current = currentUserId;

        // Reset any overlay state left over from the previous user's run.
        // Otherwise a sign-out → different sign-in would still show their
        // step list / visible flag if they hadn't completed the flow.
        setVisible(false);
        setExiting(false);
        setStep(0);
        setSteps([]);
        artistsAlreadyDoneRef.current = false;
        // Re-pending the paywall gate for this user: it must wait until decide()
        // resolves below (and any overlay is dismissed) before presenting.
        dispatch(setOnboardingActive(false));
        dispatch(setOnboardingDecided(false));

        let cancelled = false;

        const decide = async () => {
            // Clean up any device-wide legacy keys left over from earlier
            // versions of this flow. We do NOT use them to set the current
            // user's per-account key — back-filling against whoever happened
            // to be signing in at the time wrongly suppressed the import
            // prompt for brand-new accounts on a machine where any prior
            // user had completed the legacy flow.
            localStorage.removeItem(LEGACY_ONBOARDING_KEY);
            localStorage.removeItem(LEGACY_IMPORT_KEY);

            const importDoneHere = !!localStorage.getItem(importDoneKey(currentUserId));

            // Local-first: if this user already finished onboarding on this
            // device, never re-show it here — independent of whether the
            // server PATCH that records `artistsOnboardedAt` ever landed.
            // Reads localStorage AND (on native) the durable Preferences
            // mirror, so iOS storage eviction can't resurrect onboarding.
            const artistsDoneHere = readArtistsDone(currentUserId);

            // Fetch server prefs to know if the artists step has ever been done
            // for this account (cross-device truth). Retry once: the call often
            // fails on a cold-start blip, and a swallowed failure used to fall
            // back to "not done" and re-show onboarding. artistsAlreadyDoneRef
            // tracks the SERVER value specifically — it gates the no-op PATCH
            // in complete().
            let artistsDoneOnServer = false;
            let serverReachable = false;
            for (let attempt = 1; attempt <= 2 && !cancelled; attempt++) {
                try {
                    const result = window.electron?.library?.getPreferences
                        ? await refreshAndRetry((token) => window.electron.library.getPreferences(token))
                        : null;
                    const payload = result?.data || result;
                    artistsDoneOnServer = !!payload?.artistsOnboardedAt;
                    serverReachable = true;
                    break;
                } catch {
                    if (attempt < 2) await new Promise((r) => setTimeout(r, 500));
                }
            }

            if (cancelled) return;
            artistsAlreadyDoneRef.current = artistsDoneOnServer;
            const artistsDone = artistsDoneHere || artistsDoneOnServer;

            // Compose the visible flow. Web has no import step at all.
            const needImport  = !isWeb && !importDoneHere;
            // Only OFFER the artists step when we have a definite "not done"
            // signal. If the server was unreachable AND we have no local proof
            // of completion, DON'T fail open — re-showing onboarding to a
            // returning user during a transient cold-start failure is the bug
            // we're fixing. Defer the decision to a future launch (decide()
            // re-runs next cold start once the server is up).
            const needArtists = !artistsDone && serverReachable;
            if (!needImport && !needArtists) {
                // Decided: nothing to show. Unblock the paywall immediately.
                dispatch(setOnboardingDecided(true));
                return;
            }

            const composed = ['welcome'];
            if (needImport)  composed.push('import');
            if (needArtists) composed.push('artists');
            composed.push('done');

            setSteps(composed);
            setVisible(true);
            track('onboarding_started');
            // Decided: onboarding will show. The paywall stays gated by
            // onboardingActive (set by the visible-mirror effect) until the
            // user dismisses/finishes, then presents.
            dispatch(setOnboardingDecided(true));

            // Pre-warm the djpal token so the artists step is instant
            if (needArtists && !localStorage.getItem('djpal:token')) {
                dispatch(connectDjpal());
            }
        };

        decide();
        return () => { cancelled = true; };
    }, [isAuthenticated, appReady, accessToken, currentUserId, dispatch]);

    // ── Detect DJ software (desktop only, and only if import step is shown) ─
    useEffect(() => {
        if (!visible || isWeb) return;
        if (!steps.includes('import')) return;
        const detect = async () => {
            const d = {};
            await Promise.all([
                window.electron.rekordbox.checkXmlExists().then(r => { d.rekordbox = r; }).catch(() => { d.rekordbox = { exists: false }; }),
                window.electron.serato.checkExists().then(r => { d.serato = r; }).catch(() => { d.serato = { exists: false }; }),
                window.electron.traktor.checkExists().then(r => { d.traktor = r; }).catch(() => { d.traktor = { exists: false }; }),
                window.electron.virtualdj.checkExists().then(r => { d.virtualdj = r; }).catch(() => { d.virtualdj = { exists: false }; }),
            ]);
            setDetectedPlatforms(d);
        };
        detect();
    }, [visible, steps]);

    // ── Load popular artists when on artists step and token is available ────
    useEffect(() => {
        if (!visible || currentStep !== 'artists') return;
        if (popularArtists.length > 0) return;

        const token = djpalToken || localStorage.getItem('djpal:token');
        if (!token) {
            dispatch(connectDjpal());
            return;
        }

        const load = async () => {
            setIsLoadingArtists(true);
            try {
                // Try the dedicated popular artists endpoint first
                const result = await window.electron.djpal.getPopularArtists(token);
                if (result?.results?.length) {
                    setPopularArtists(result.results);
                    setIsLoadingArtists(false);
                    return;
                }
            } catch (e) {
                console.warn('[Onboarding] getPopularArtists failed, trying fallback:', e);
            }

            // Popular artists endpoint not yet deployed — leave list empty so user searches
            console.info('[Onboarding] /artists/popular not available yet. Deploy music-info-server to enable.');
            setIsLoadingArtists(false);
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, currentStep, djpalToken, popularArtists.length, dispatch]);

    // ── Artist autocomplete search ───────────────────────────────────────────
    useEffect(() => {
        const token = djpalToken || localStorage.getItem('djpal:token');
        if (!token || !searchQuery.trim()) {
            setSearchResults([]);
            return;
        }
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(async () => {
            try {
                const result = await window.electron.djpal.autocompleteArtist(token, searchQuery);
                setSearchResults(result?.results || []);
            } catch {
                setSearchResults([]);
            }
        }, 280);
        return () => clearTimeout(searchTimeout.current);
    }, [searchQuery, djpalToken]);

    // ── Navigation ───────────────────────────────────────────────────────────
    const goNext = useCallback(() => {
        // As soon as the user moves PAST the artists step (followed some or
        // skipped), record the local per-user flag — so even if they then
        // reload / kill the app on the final "done" screen instead of
        // clicking "Start exploring", onboarding won't re-show next launch.
        if (steps[step] === 'artists' && currentUserId) {
            writeArtistsDone(currentUserId);
        }
        if (step < steps.length - 1) {
            setStep(s => s + 1);
        }
    }, [step, steps, currentUserId]);

    const complete = useCallback(() => {
        // Per-(user, device): don't ask THIS user to import on THIS
        // machine again. A different account on the same device still
        // gets its own one-time prompt. On web there's no import step
        // but writing the key is harmless.
        if (!isWeb && currentUserId) {
            localStorage.setItem(importDoneKey(currentUserId), 'done');
        }

        // Per-(user) local flag, written synchronously so this device never
        // re-shows onboarding for this user even if the server PATCH below is
        // lost. This is the reliable local guarantee; the server write is
        // best-effort cross-device sync. (web included — the import step is
        // skipped on web but the artists step still runs.)
        if (currentUserId) {
            writeArtistsDone(currentUserId);
        }

        // Per-account: only PATCH the server if the artists step wasn't
        // already done on the server (avoids a no-op write on second-device
        // sign-ins). This is the ONLY cross-install memory — localStorage is
        // wiped on reinstall — so it must actually land. Go through
        // refreshAndRetry (resolves + refreshes the token; the bare
        // accessToken is null on web/native and relied on a shim fallback)
        // and log the outcome so a failed persist is visible in the device
        // web inspector instead of silently re-showing onboarding forever.
        if (!artistsAlreadyDoneRef.current) {
            artistsAlreadyDoneRef.current = true;
            (async () => {
                try {
                    const res = await refreshAndRetry((token) =>
                        window.electron.library.updatePreferences(token, {
                            artistsOnboardedAt: new Date().toISOString(),
                        })
                    );
                    if (res?.error) {
                        console.warn('[Onboarding] failed to persist artistsOnboardedAt:', res.error);
                    } else {
                        console.info('[Onboarding] artistsOnboardedAt persisted');
                    }
                } catch (e) {
                    console.warn('[Onboarding] persist artistsOnboardedAt threw:', e?.message || e);
                }
            })();
        }

        setExiting(true);
        setTimeout(() => setVisible(false), 400);
    }, [currentUserId]);

    const handleGoToMigrate = useCallback(() => {
        dispatch(setActiveCategory('library'));
        dispatch(setActiveTab('migrate-library'));
        complete();
    }, [dispatch, complete]);

    // ── Follow toggle ────────────────────────────────────────────────────────
    const handleFollowToggle = useCallback((artist) => {
        const name = artist.name;
        setLocalFollowed(prev => {
            const next = new Set(prev);
            // quiet: picking artists here is a bulk flow — no confirmation cards.
            if (next.has(name)) {
                next.delete(name);
                dispatch(unfollowArtist({ artistName: name, quiet: true }));
            } else {
                next.add(name);
                dispatch(followArtist({ ...artist, from: 'onboarding', quiet: true }));
            }
            return next;
        });
    }, [dispatch]);

    // Publish onboarding visibility so the native paywall auto-present (App.js)
    // holds until onboarding is dismissed, then presents — instead of being
    // dispatched behind this overlay and lost on Skip.
    useEffect(() => {
        dispatch(setOnboardingActive(visible));
        return () => { dispatch(setOnboardingActive(false)); };
    }, [visible, dispatch]);

    if (!visible) return null;

    return (
        <div className={`ob-overlay${exiting ? ' ob-exiting' : ''}`}>
            <div className="ob-bg-gradient" />
            <div className="ob-bg-noise" />

            <div className="ob-card">
                {/* Skip button */}
                {currentStep !== 'done' && (
                    <button className="ob-skip-btn" onClick={() => { track('onboarding_skipped', { step: currentStep }); complete(); }}>
                        Skip
                    </button>
                )}

                {/* Step content */}
                <div className={`ob-content-wrap step-${currentStep}`}>
                    {currentStep === 'welcome' && (
                        <WelcomeStep onNext={goNext} />
                    )}
                    {currentStep === 'import' && (
                        <ImportStep
                            detectedPlatforms={detectedPlatforms}
                            onNext={goNext}
                        />
                    )}
                    {currentStep === 'artists' && (
                        <ArtistsStep
                            popularArtists={popularArtists}
                            searchResults={searchResults}
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            localFollowed={localFollowed}
                            onFollowToggle={handleFollowToggle}
                            isLoading={isLoadingArtists}
                            onNext={goNext}
                        />
                    )}
                    {currentStep === 'done' && (
                        <DoneStep
                            onComplete={() => { track('onboarding_completed'); complete(); }}
                            followCount={localFollowed.size}
                        />
                    )}
                </div>

                {/* Step indicator */}
                <div className="ob-step-dots">
                    {steps.map((s, i) => (
                        <div
                            key={s}
                            className={`ob-dot ${i === step ? 'active' : ''} ${i < step ? 'past' : ''}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default OnboardingFlow;
